import React, { useEffect, useRef, useState } from "react";
import { Box, InputAdornment, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { RequireAuth } from "@/components/RequireAuth";
import SearchSvg from "@/assets/svg/SearchSvg";
import { setParams } from "@/store/slice/postParamsSlice";
import theme from "@/theme";
import { styles } from "./styleSearchWethemrkt";

const SearchWethemrkt: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Bug 2: initialize input from URL params on load
  const [leftSearchValue, setLeftSearchValue] = useState(() => {
    const p = new URLSearchParams(location.search);
    const text = p.get("text") ?? "";
    const author = p.get("author") ?? "";
    if (author) return `@${author}${text ? ` ${text}` : ""}`;
    return text;
  });

  const [postFocused, setPostFocused] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Bug 6: clear debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const handleEnterSearch = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const mentionMatch = trimmed.match(/@(\w+)/);

    if (mentionMatch) {
      const author = mentionMatch[1];
      // Bug 7: strip ALL @mentions from remaining text, not just the first
      const remainingText = trimmed.replace(/@\w+\s*/g, "").trim();
      const params: { author: string; text?: string; page: number } = { author, page: 1 };
      let queryString = `author=${encodeURIComponent(author)}`;

      if (remainingText) {
        params.text = remainingText;
        queryString += `&text=${encodeURIComponent(remainingText)}`;
      }

      dispatch(setParams(params));
      navigate(`/?${queryString}`);
    } else {
      dispatch(setParams({ text: trimmed, author: "", page: 1 }));
      navigate(`/?text=${encodeURIComponent(trimmed)}`);
    }
    // Bug 5: removed setPostFocused(false) — input still focused, blur handles it
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLeftSearchValue(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (!value.trim()) {
      dispatch(setParams({ text: "", author: "", page: 1 }));
      navigate("/");
      return;
    }

    // Bug 4: debounce for all inputs — 500ms for text, 2000ms for @mentions
    const delay = value.includes("@") ? 2000 : 500;
    debounceTimer.current = setTimeout(() => {
      handleEnterSearch(value);
    }, delay);
  };

  return (
    <Box sx={styles.leftBlock}>
      <Box sx={styles.postFieldWrapper(postFocused)}>
        <RequireAuth>
          <TextField
            placeholder="Please add your search information and click enter"
            variant="standard"
            value={leftSearchValue}
            onChange={handleChange}
            onFocus={() => setPostFocused(true)}
            onBlur={() => setPostFocused(false)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchSvg color={postFocused ? theme.palette.custom.mainBlue : "#9DA6B0"} />
                </InputAdornment>
              ),
              sx: styles.inputField,
              disableUnderline: true,
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (debounceTimer.current) clearTimeout(debounceTimer.current);
                handleEnterSearch(leftSearchValue);
              }
            }}
          />
        </RequireAuth>
      </Box>
    </Box>
  );
};

export default SearchWethemrkt;
